// ─── Materialisasi varian PO saat menambah ke keranjang ─────────────────────
//
// Satu-satunya pemanggil RPC `resolve_or_create_preorder_variant` (mig 3630,
// diperbaiki mig 4640) dari browser. Hidup TERPISAH dari `public-data.ts`
// dengan sengaja: modul itu memakai klien anon TANPA SESI
// (`@/lib/supabase/client`, `persistSession:false`) untuk katalog publik,
// sedangkan RPC ini MENULIS baris dan EXECUTE-nya hanya untuk `authenticated`.
//
// BUG LIVE 2026-09-02 (ZURIAH by MOSLEM DAILY — produk PO pertama yang lahir
// tanpa satu pun baris varian, 16×5×10 = 800 kombinasi): fungsi ini dulu
// tinggal di `public-data.ts` dan menembak RPC lewat klien anon itu, sehingga
// SETIAP pembeli — yang sudah login sekalipun — mengirim JWT `anon` dan
// ditolak 42501 (`permission denied for function …`, 8 dari 8 panggilan di
// edge log berbalas 401). Klien lalu menelan errornya menjadi dialog "Ada
// keranjang produk lain" pada keranjang kosong.
//
// Sekarang memakai klien ber-sesi (`createBrowserSupabase()` — scope
// otomatis dari path; di halaman produk storefront itu sesi PEMBELI), dan
// kegagalannya diklasifikasi (`classifyPreorderVariantFailure`) supaya tamu
// diminta MASUK, bukan disuruh mengosongkan keranjang yang memang kosong.
//
// Scope toko TIDAK difilter di sini dan itu disengaja: batas tenant untuk
// RPC yang bisa dipanggil langsung lewat PostgREST tak pernah bisa dipasang
// di klien. Batasnya ada di dalam RPC — SECURITY DEFINER yang menolak bila
// konteks toko pemanggil (`current_store_id()`, atau `service_store_context()`
// untuk service_role ber-header) NULL atau berbeda dari `products.store_id`
// (FAIL-CLOSED sejak mig 4650; sebelumnya cabang NULL dilewati). Yang
// dipulangkan hanya SATU uuid untuk produk yang sudah ditampilkan halaman
// ini — tak ada himpunan baris lintas-toko yang bisa bocor (beda kelas dari
// pembaca anon yang dijaga `storeScopedQueries.test.ts`).

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { withTimeout } from "@/lib/withTimeout";
import {
  classifyPreorderVariantFailure,
  type PreorderVariantResolution,
} from "@/lib/lazyVariants";

// Cermin `PUBLIC_FETCH_TIMEOUT_MS` public-data.ts: supabase-js tak punya
// timeout; tombol keranjang tak boleh menggantung selamanya.
const RESOLVE_TIMEOUT_MS = 8000;

/**
 * MATERIALISASI SAAT DIPESAN (A) — pastikan baris varian untuk kombinasi ini
 * ADA, membuatnya bila perlu, lalu pulangkan id-nya.
 *
 * DIPANGGIL HANYA SAAT MENAMBAH KE KERANJANG, tidak saat memilih opsi.
 * Membuat baris sekadar karena pembeli menjelajah kombinasi akan
 * memateralisasi kartesius lewat pintu belakang — persis padding yang
 * dihilangkan mig 3630 (45.570 baris inert di PROD).
 *
 * Tidak pernah melempar: setiap kegagalan pulang sebagai `{ ok:false, reason }`
 * supaya komponen memilih dialog yang benar, bukan menebak.
 */
export async function resolveOrCreatePreorderVariant(
  productId: string,
  optionIds: string[]
): Promise<PreorderVariantResolution> {
  if (!productId || optionIds.length === 0) {
    return { ok: false, reason: "UNAVAILABLE" };
  }
  const db = createBrowserSupabase();
  if (!db) return { ok: false, reason: "UNAVAILABLE" };

  try {
    const { data, error, status } = await withTimeout(
      db.rpc("resolve_or_create_preorder_variant", {
        p_product_id: productId,
        p_option_ids: optionIds,
      }),
      RESOLVE_TIMEOUT_MS
    );
    if (error) {
      return {
        ok: false,
        reason: classifyPreorderVariantFailure({
          code: error.code,
          status,
          message: error.message,
        }),
        message: error.message,
      };
    }
    if (typeof data !== "string" || !data) {
      return { ok: false, reason: "UNAVAILABLE" };
    }
    return { ok: true, id: data };
  } catch {
    // Timeout/jaringan. Pemanggil menahan tombol pesan (varian belum pasti);
    // menebak id jauh lebih buruk daripada meminta pembeli mencoba lagi.
    return { ok: false, reason: "UNAVAILABLE" };
  }
}
